import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FileText, Download, Settings } from 'lucide-react';
import { toast } from 'sonner';

const BrandedReportExporter = ({ reducedMotion }) => {
  const [reportConfig, setReportConfig] = useState({
    title: 'AutoMind Analytics Report',
    date_range: '7',
    include_charts: true,
    include_raw_data: false,
    logo_url: ''
  });
  const [exporting, setExporting] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/export/branded-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportConfig)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AutoMind_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="branded-report-exporter-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <FileText className="w-5 h-5 text-rose-400" />
          Branded Report Exporter
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Generate professional PDF reports with your branding
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Report Configuration */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-2 block">Report Title</label>
              <Input
                value={reportConfig.title}
                onChange={(e) => setReportConfig({ ...reportConfig, title: e.target.value })}
                placeholder="Enter report title"
                className="bg-slate-800 border-slate-700"
                data-testid="report-title-input"
              />
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-2 block">Date Range (days)</label>
              <select
                value={reportConfig.date_range}
                onChange={(e) => setReportConfig({ ...reportConfig, date_range: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100"
                data-testid="date-range-select"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-2 block">Logo URL (optional)</label>
              <Input
                value={reportConfig.logo_url}
                onChange={(e) => setReportConfig({ ...reportConfig, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
                className="bg-slate-800 border-slate-700"
                data-testid="logo-url-input"
              />
            </div>
          </div>

          {/* Options */}
          <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reportConfig.include_charts}
                onChange={(e) => setReportConfig({ ...reportConfig, include_charts: e.target.checked })}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-rose-500 focus:ring-rose-500"
                data-testid="include-charts-checkbox"
              />
              <span className="text-sm text-slate-300">Include Charts & Visualizations</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reportConfig.include_raw_data}
                onChange={(e) => setReportConfig({ ...reportConfig, include_raw_data: e.target.checked })}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-rose-500 focus:ring-rose-500"
                data-testid="include-raw-data-checkbox"
              />
              <span className="text-sm text-slate-300">Include Raw Data Tables</span>
            </label>
          </div>

          {/* Export Button */}
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-medium"
            data-testid="export-report-button"
          >
            {exporting ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export PDF Report
              </>
            )}
          </Button>

          {/* Info */}
          <div className="p-4 bg-ink-500 rounded-lg border border-ink-300 dark:border-ink-700">
            <p className="text-xs text-slate-400">
              <strong className="text-ink-500">Pro Tip:</strong> Reports include executive summary, key metrics, visualizations, and trend analysis. Perfect for stakeholder presentations.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandedReportExporter;